import { Client, fetchExchange, OperationResult } from "@urql/core";
import { authExchange } from "@urql/exchange-auth";
import { getIdToken } from "@kahunmedical/user-management/";
import {
  AskPromptInSessionMutationVariables,
  ChatReply,
  DuplicateResult,
  Exact,
  ExchangeFeedback,
  ExchangeFeedbackInput,
  FeedbackReportExchange,
  MutationAskQuestionInSessionArgs,
  PredefinedInsight,
  PromptReply,
  QueryStartChatArgs,
  QuestionOptions,
  Result,
  Session,
  SessionByCaseIdReply,
  SessionExchange,
  SessionItem,
  SessionStartReply,
  StartReply,
} from "./types/graphql";
import {
  askQuestionInSession,
  duplicateSession,
  getInitInfo,
  searchSessionQuery,
  getSessionsQuery,
  removeSession,
  restoreAnySession,
  restoreSession,
  StartChatQuery,
  startSession,
  startSessionByCaseIdWithCheck,
  startSessionByCaseIdNoCheck,
  updateExchangeFeedback,
  userTakeover,
  answerQuestion,
  answerPrompt,
  askPromptInSession,
  updatePromptFeedback,
  getAppList,
  guidelineFilterableKeys,
  feedbackReport,
  clinicExchanges,
} from "./queries";
import {
  getRagEndpoint,
  getEndpointUrl,
} from "fe-web-components/settings/RagSettings";
import { INLINE_CITATIONS_STEAM_MODE } from "../constants";
import { SessionType } from "../types/types";

export type ConfigurableRagDaoHeader = "kahun_rag_app" | "traceCalls";
export class RagDao {
  public client?: Client;
  private headers = {};
  private env = getRagEndpoint();

  constructor() {
    const getFetchOptions = () => {
      return { headers: this.headers };
    };
    this.client = new Client({
      url: getEndpointUrl(this.env),
      fetchOptions: getFetchOptions,
      exchanges: [
        authExchange(async (utils) => {
          //Use Id token, so we can use the email as a key to the sessions
          let accessToken = await getIdToken();
          return {
            addAuthToOperation(operation) {
              return utils.appendHeaders(operation, {
                Authorization: `Bearer ${accessToken}`,
              });
            },
            async refreshAuth() {
              accessToken = await getIdToken();
            },
            didAuthError(error, _operation) {
              return error?.graphQLErrors?.length > 0;
              //return error.graphQLErrors.some(
              //   (e) => e.extensions?.code === "FORBIDDEN",
              // );
            },
            willAuthError(operation) {
              if (!accessToken) return true;
              //check if the token is about to expire
              const payload = accessToken.split(".")[1];
              const decodedPayload = JSON.parse(atob(payload).toString());
              const currentTimestamp = Math.floor(Date.now() / 1000);
              return decodedPayload?.exp + 60 < currentTimestamp;
            },
          };
        }),
        fetchExchange,
      ],
    });
  }
  public setRAGCallHeader(header: ConfigurableRagDaoHeader, value: string) {
    if (value !== undefined && value !== null) {
      this.headers[header] = value;
    } else {
      delete this.headers[header];
    }
  }
  public getEnv() {
    return this.env;
  }
  async geInitialInfo() {
    const response = await this.client.query(getInitInfo, {}).toPromise();
    return response.data;
  }
  async getAppList() {
    const response = await this.client.query(getAppList, {}).toPromise();
    return response.data;
  }
  async getGuidelineFilters(app?: string, corpus?: [string]) {
    const response = await this.client
      .query(guidelineFilterableKeys, { corpus, app })
      .toPromise();
    return response.data.guidelineFilterableKeys;
  }
  async startChat(patientCase: string, modelver: string): Promise<StartReply> {
    const variables: QueryStartChatArgs = { patientCase, modelver };
    const response = await this.client
      .query(StartChatQuery, variables)
      .toPromise();

    return response.data.startChat;
  }
  async answerQuestionInSession(
    context: any,
    question: string,
    options: QuestionOptions,
    sessionId: string,
  ): Promise<ChatReply> {
    const variables: MutationAskQuestionInSessionArgs = {
      context,
      question,
      options: {
        ...options,
        streamMode: INLINE_CITATIONS_STEAM_MODE,
      },
      sessionId,
    };
    const response = await this.client
      .mutation(askQuestionInSession, variables)
      .toPromise();
    return response.data.askQuestionInSession;
  }

  async getSessions(showOnlyActive: boolean): Promise<SessionItem[]> {
    const response = await this.client
      .query(getSessionsQuery, { showOnlyActive })
      .toPromise();
    if (response.error) throw new Error(response.error.message);
    return response.data.sessions;
  }

  async searchSessions(
    showOnlyActive: boolean,
    username: string,
    caseId: string,
    clinicId: string,
  ): Promise<SessionItem[]> {
    const response = await this.client
      .query(searchSessionQuery, { showOnlyActive, username, caseId, clinicId })
      .toPromise();
    if (response.error) throw new Error(response.error.message);
    return response.data.sessionsSearch;
  }

  async startSession(
    patientCase: string,
    modelver: string,
    generateSuggestion: boolean,
    clinicId?: string,
  ): Promise<SessionStartReply> {
    const variables = {
      patientCase,
      modelver,
      generateSuggestion,
      clinicId,
    };
    const response = await this.client
      .mutation(startSession, variables)
      .toPromise();
    return response.data.startSession;
  }

  async restoreSession(
    sessionId: string,
    allowAnyUser: boolean,
  ): Promise<Session> {
    const variables = { sessionId };
    const response: OperationResult<
      any,
      Exact<{ sessionId?: string }>
    > = await this.client
      .query(!allowAnyUser ? restoreSession : restoreAnySession, variables)
      .toPromise();
    return !allowAnyUser
      ? response.data.restoreSession
      : response.data.restoreAnySession;
  }

  async startSessionByCaseId(
    allowAnyUser: boolean, //only admins can restore sessions of other users
    patientCase: string,
    modelver: string,
    generateSuggestion: boolean,
    caseId: string,
    clinicId: string,
  ): Promise<SessionByCaseIdReply> {
    const variables = {
      patientCase,
      modelver,
      generateSuggestion,
      caseId,
      clinicId,
    };
    const response: OperationResult<any> = await this.client
      .mutation(
        !allowAnyUser
          ? startSessionByCaseIdWithCheck
          : startSessionByCaseIdNoCheck,
        variables,
      )
      .toPromise();
    return !allowAnyUser
      ? response.data.startSessionByCaseIdWithCheck
      : response.data.startSessionByCaseIdNoCheck;
  }

  async removeSession(sessionId: string): Promise<Result> {
    const variables = { sessionId };
    const response = await this.client
      .mutation(removeSession, variables)
      .toPromise();
    return response.data.removeSession;
  }

  async duplicateSession(
    sessionId: string,
    takeover: boolean,
  ): Promise<DuplicateResult> {
    const variables = { sessionId, takeover };
    const response = await this.client
      .mutation(duplicateSession, variables)
      .toPromise();
    return response.data.duplicateSession;
  }

  async updateExchangeFeedback(
    sessionId: string,
    index: number,
    feedback: ExchangeFeedbackInput,
  ): Promise<ExchangeFeedback> {
    const variables = { sessionId, index, feedback };
    const response = await this.client
      .mutation(updateExchangeFeedback, variables)
      .toPromise();
    return response.data.updateExchangeFeedback.feedback;
  }

  async updatePromptFeedback(
    sessionId: string,
    customPrompt: string,
    predefinedInsight: PredefinedInsight,
    feedback: ExchangeFeedbackInput,
  ): Promise<ExchangeFeedback> {
    const variables = {
      sessionId,
      promptName: customPrompt,
      predefinedInsight,
      feedback,
    };
    const response = await this.client
      .mutation(updatePromptFeedback, variables)
      .toPromise();
    return response.data.updatePromptFeedback.feedback;
  }

  async userTakeover(sessionId: string): Promise<Result> {
    const variables = { sessionId };
    const response = await this.client
      .mutation(userTakeover, variables)
      .toPromise();
    return response.data.userTakeover;
  }

  //ask a question without creating a new exchange
  async answerQuestion(
    context: any,
    question: string,
    options: QuestionOptions,
  ): Promise<ChatReply> {
    const variables = {
      context,
      question,
      options: {
        ...options,
        streamMode: INLINE_CITATIONS_STEAM_MODE,
      },
    };
    const response = await this.client
      .query(answerQuestion, variables)
      .toPromise();
    return response.data.answerQuestion;
  }

  async answerPrompt(
    context: any,
    options: QuestionOptions,
    predefinedInsight?: PredefinedInsight,
    prompt?: string,
    promptName?: string,
  ): Promise<PromptReply> {
    const variables = {
      context,
      options,
      predefinedInsight,
      prompt,
      promptName,
    };

    const response = await this.client
      .query(answerPrompt, variables)
      .toPromise();
    if (response.error) throw new Error(response.error.message);
    return response.data.answerPrompt;
  }

  async askPromptInSession(
    context: any,
    options: QuestionOptions,
    predefinedInsight?: PredefinedInsight,
    prompt?: string,
    promptName?: string,
    sessionId?: string,
  ): Promise<PromptReply> {
    const variables: AskPromptInSessionMutationVariables = {
      context,
      options,
      predefinedInsight,
      prompt,
      promptName,
      sessionId,
    };
    const response = await this.client
      .mutation(askPromptInSession, variables)
      .toPromise();
    if (response.error) throw new Error(response.error.message);
    return response.data.askPromptInSession;
  }

  async getFeedBackReport(
    clinicId: string,
    user: string,
    fromDate: string,
    toDate: string,
    sessionType: SessionType,
  ): Promise<FeedbackReportExchange[]> {
    const response = await this.client
      .query(feedbackReport, { clinicId, user, fromDate, toDate, sessionType })
      .toPromise();
    if (response.error) throw new Error(response.error.message);
    return response.data.feedbackReport;
  }

  async getClinicExchanges(
    clinicId: string,
    fromDate: string,
    toDate: string,
    sessionType: SessionType,
  ): Promise<SessionExchange[]> {
    const response = await this.client
      .query(clinicExchanges, { clinicId, fromDate, toDate, sessionType })
      .toPromise();
    if (response.error) throw new Error(response.error.message);
    return response.data.clinicExchanges;
  }
}
